// src/crashReports/performance-integration.ts
import { PerformanceMonitor } from '../utils';
import { ServiceWorkerCrashMonitor } from './serviceWorkerCrashMonitor';
import type { HeartbeatData, PerformanceMetrics } from '../types';
import { logger as moduleLogger } from '@/crashReports/utils/logger';
const logger = moduleLogger.createSubLogger('EnhancedCrashMonitor');
/**
 * 集成性能监控的崩溃监测器
 */
export class EnhancedCrashMonitor extends ServiceWorkerCrashMonitor {
  private performanceMonitor: PerformanceMonitor;
  private performanceInterval: ReturnType<typeof setInterval> | null = null;

  constructor(config?: any) {
    super(config);
    this.performanceMonitor = new PerformanceMonitor(config);
  }

  /**
   * 启动增强的崩溃监测（包含性能监控）
   */
  async start(): Promise<void> {
    await super.start();

    // 启动性能监控
    // this.startPerformanceMonitoring();

    logger.log('🚀 增强崩溃监测已启动（包含性能监控）');
  }

  /**
   * 停止增强的崩溃监测
   */
  stop(): void {
    super.stop();
    // this.stopPerformanceMonitoring();
    logger.log('�� 增强崩溃监测已停止');
  }

  /**
   * 启动性能监控
   */
  private startPerformanceMonitoring() {
    // 每5秒收集一次性能指标
    this.performanceInterval = setInterval(() => {
      const metrics =
        this.performanceMonitor.getMetrics() as PerformanceMetrics;
      const issues = this.performanceMonitor.checkPerformanceIssues();

      // 记录性能指标
      logger.log('📊 性能指标:', {
        memoryUsage: metrics.memory.memoryUsage.toFixed(2) + '%',
        frameRate: metrics.runtime.frameRate + ' FPS',
        uptime: (metrics.runtime.uptime / 1000).toFixed(0) + 's',
      });

      // 检查性能问题
      if (issues.length > 0) {
        logger.warn('⚠️ 检测到性能问题:', issues);

        // 如果有严重问题，可以触发额外的处理
        const highSeverityIssues = issues.filter((i) => i.severity === 'high');
        if (highSeverityIssues.length > 0) {
          this.handlePerformanceIssues(highSeverityIssues, metrics);
        }
      }
    }, 5000);
  }

  getHeartbeatData(): HeartbeatData {
    const metrics = this.performanceMonitor.getMetrics(['memory', 'runtime']);
    const heartbeatData: HeartbeatData = {
      timestamp: Date.now(),
      sessionId: this.getSessionId(),
      pageUrl: window.location.href,
      userAgent: navigator.userAgent,
      performanceMetrics: JSON.stringify(metrics),
    };

    return heartbeatData;
  }
  /**
   * 停止性能监控
   */
  private stopPerformanceMonitoring() {
    if (this.performanceInterval) {
      clearInterval(this.performanceInterval);
      this.performanceInterval = null;
    }
  }

  /**
   * 处理性能问题
   */
  private handlePerformanceIssues(issues: any[], metrics: any) {
    logger.error('🚨 检测到严重性能问题:', issues);

    // 可以在这里添加额外的处理逻辑
    // 比如发送性能警告、记录日志等

    // 示例：发送性能警告
    this.sendPerformanceWarning(issues, metrics);
  }

  /**
   * 发送性能警告
   */
  private sendPerformanceWarning(issues: any[], metrics: any) {
    // 这里可以实现发送性能警告的逻辑
    // 比如调用API上报性能问题
    logger.log('📤 发送性能警告:', {
      issues,
      metrics: {
        memoryUsage: metrics.memory.memoryUsage,
        frameRate: metrics.runtime.frameRate,
        timestamp: new Date().toISOString(),
      },
    });
  }

  /**
   * 获取性能报告
   */
  getPerformanceReport(): string {
    return this.performanceMonitor.exportReport();
  }

  /**
   * 获取当前性能指标
   */
  getCurrentMetrics() {
    return this.performanceMonitor.getMetrics();
  }

  /**
   * 检查性能问题
   */
  checkPerformanceIssues() {
    return this.performanceMonitor.checkPerformanceIssues();
  }
}
